export interface MapeamentoUsoSelecao {
  filter_id: number;
  selecao: string;
  boletins: number;
  licitacoes: number;
  acompanhamentos: number;
}

export interface MapeamentoUsoSerieMensal {
  mes: string;
  licitacoes: number;
  acompanhamentos: number;
  boletins: number;
}

export interface MapeamentoUso {
  venda: {
    cliente_id: number;
    venda_id: number;
    inicio: string;
    fim: string;
    ciclo: string | null;
    ciclo_meses: number | null;
    janela_meses: number | null;
    valor_total: number | null;
  };
  cliente: {
    contato_nome: string | null;
    cliente_desde: string | null;
  };
  boletim: {
    selecoes: MapeamentoUsoSelecao[];
    serie_mensal: MapeamentoUsoSerieMensal[];
  };
  monitorar_chat: {
    licitacoes_monitoradas: number;
    mensagens_recebidas: number;
  };
  gerenciar_licitacoes: {
    licitacoes_gerenciadas: number;
  };
  assistente_juridico: {
    respostas_juridicas: number;
  };
  consultas_juridicas: {
    consultas_respondidas: number;
  };
  pergunte_ao_edital: {
    respostas_entregues: number;
    editais_consultados: number;
  };
  resultados: {
    resultados_mencionados: number;
  };
}

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

export interface RenewalMonthly {
  label: string;
  licitacoes: number;
  acompanhamentos: number;
  boletins: number;
}

export interface RenewalUser {
  firstName: string;
  cycleMonths: number;
  offerEndsAt: number;
  stats: {
    opportunities: number;
    followups: number;
    bulletins: number;
    managedBiddings: number;
    chatsMonitored: number;
    chatMessages: number;
    legalConsultations: number;
    biddingQuestions: number;
  };
  monthly: RenewalMonthly[];
}

const totalSelecoes = (
  mapeamento: MapeamentoUso,
  campo: 'licitacoes' | 'acompanhamentos' | 'boletins',
) => mapeamento.boletim.selecoes.reduce((total, selecao) => total + selecao[campo], 0);

export const buildUser = (mapeamento: MapeamentoUso): RenewalUser => ({
  firstName: (mapeamento.cliente.contato_nome ?? '').split(' ')[0],
  cycleMonths: mapeamento.venda.janela_meses ?? (mapeamento.venda.ciclo_meses === 6 ? 6 : 12),
  offerEndsAt: new Date(mapeamento.venda.fim.replace(' ', 'T')).getTime(),
  stats: {
    opportunities: totalSelecoes(mapeamento, 'licitacoes'),
    followups: totalSelecoes(mapeamento, 'acompanhamentos'),
    bulletins: totalSelecoes(mapeamento, 'boletins'),
    managedBiddings: mapeamento.gerenciar_licitacoes.licitacoes_gerenciadas,
    chatsMonitored: mapeamento.monitorar_chat.licitacoes_monitoradas,
    chatMessages: mapeamento.monitorar_chat.mensagens_recebidas,
    legalConsultations: mapeamento.consultas_juridicas.consultas_respondidas,
    biddingQuestions: mapeamento.pergunte_ao_edital.editais_consultados,
  },
  monthly: (mapeamento.boletim.serie_mensal ?? []).map((item) => {
    const mesAbrev = MESES[Number(item.mes.slice(5, 7)) - 1];
    return {
      label: mesAbrev.charAt(0).toUpperCase() + mesAbrev.slice(1),
      licitacoes: item.licitacoes,
      acompanhamentos: item.acompanhamentos,
      boletins: item.boletins ?? 0,
    };
  }),
});

export interface RenewalCycle {
  id: string;
  label: string;
  installments: number;
}

export interface RenewalPlanPricing {
  full: number;
  discounted: number;
}

export interface RenewalPlanData {
  id: string;
  name: string;
  pricing: { [cycleId: string]: RenewalPlanPricing };
}

export interface RenewalPlanFeature {
  name: string;
  ia?: boolean;
  availability?: { [planId: string]: boolean };
}

export const cycles: RenewalCycle[] = [
  { id: 'semestral', label: 'Semestral', installments: 3 },
  { id: 'anual', label: 'Anual', installments: 5 },
  { id: 'bienal', label: 'Bienal', installments: 8 },
];

export const plans: RenewalPlanData[] = [
  {
    id: 'premium',
    name: 'Plano Premium',
    pricing: {
      semestral: { full: 2007.0, discounted: 1806.3 },
      anual: { full: 3190.0, discounted: 2871.0 },
      bienal: { full: 5220.0, discounted: 4698.0 },
    },
  },
  {
    id: 'advanced',
    name: 'Plano Advanced',
    pricing: {
      semestral: { full: 2368.0, discounted: 2131.2 },
      anual: { full: 3790.0, discounted: 3411.0 },
      bienal: { full: 6221.0, discounted: 5598.9 },
    },
  },
  {
    id: 'black',
    name: 'Plano Black',
    pricing: {
      semestral: { full: 3088.0, discounted: 2779.2 },
      anual: { full: 4988.0, discounted: 4489.2 },
      bienal: { full: 8220.0, discounted: 7398.0 },
    },
  },
];

export const planFeatures: RenewalPlanFeature[] = [
  { name: 'Boletins de Licitações' },
  { name: 'Encontrar Licitações' },
  { name: 'Encontrar Acompanhamentos' },
  { name: 'Gerenciar Licitações' },
  { name: 'ConLicitaGO' },
  { name: 'Análise de Mercado' },
  { name: 'Concorrentes' },
  { name: 'Jurídico Fácil' },
  { name: 'Suporte Jurídico' },
  { name: 'Gerenciar Documentos' },
  { name: 'Monitoramento de Chat' },
  { name: 'Licitações Estratégicas' },
  { name: 'Contratos' },
  { name: 'Ata de Registro de Preços' },
  { name: 'Pergunte ao Edital', ia: true },
  { name: 'Especialista Virtual', ia: true },
  {
    name: 'Dr. Licita',
    ia: true,
    availability: { premium: false, advanced: true, black: true },
  },
  {
    name: 'Robô de Lances',
    availability: { premium: false, advanced: false, black: true },
  },
];

export const heroBenefits = [
  'Continuidade total dos seus boletins, filtros e alertas personalizados',
  'Garantia de preço congelado por mais um ciclo completo',
  'Suporte jurídico e analistas dedicados, sem interrupção',
];

export const comparisonWithout = [
  'Boletins, filtros e alertas personalizados são pausados',
  'Histórico de pesquisas e licitações acompanhadas fica indisponível',
  'Sem suporte jurídico nem analista para tirar dúvidas em editais',
  'Concorrentes seguem recebendo as oportunidades antes de você',
  'Reativação posterior pelo valor cheio, sem o desconto de 10%',
];

export const comparisonRenewing = [
  'Boletins e alertas continuam chegando sem nenhuma interrupção',
  'Suporte jurídico e analistas seguem disponíveis no seu plano',
  'Você continua à frente das oportunidades públicas em tempo real',
  'Garantia de 10% de desconto aplicado imediatamente',
  'Preço congelado por mais um ciclo completo',
];

export const legalChips = [
  'Análise de editais',
  'Recursos administrativos',
  'Impugnações',
  'Pareceres jurídicos',
];

export const courseChips = ['Pregão eletrônico', 'SRP', 'Lei 14.133', 'ME/EPP'];

export const formatBRL = (value: number): string =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 });
